import React, { useCallback, useState } from "react";
import { get, useFormContext, useWatch } from "react-hook-form";
import styled, { StyledComponent } from "styled-components";
import { CheckedIconDefault } from "./icons/default/checked-icon-default";
import { UncheckedIconDefault } from "./icons/default/unchecked-icon-default";
import { UncheckedIconPatientFacing } from "./icons/patient-facing/unchecked-icon-patient-facing";
import { CheckedIconPatientFacing } from "./icons/patient-facing/checked-icon-patient-facing";
import { FormFieldType } from "storybook/utils/form";
import { ThemeType } from "storybook/utils/theme";
import { FieldErrorDefault } from "storybook/components/field-error";
import { StyledCheckboxLabelDefault } from "./themes/default";
import { StyledCheckboxLabelPatientFacing } from "./themes/patient-facing";

interface CheckboxThemeType extends ThemeType {
  name: Extract<ThemeType["name"], "default" | "patient-facing">;
}

type StyledComponentMap = Record<
  CheckboxType<any>["theme"],
  StyledComponent<any, any>
>;

const styledCheckboxError: StyledComponentMap = {
  default: FieldErrorDefault,
  "patient-facing": FieldErrorDefault,
};
const styledCheckboxLabel: StyledComponentMap = {
  default: StyledCheckboxLabelDefault,
  "patient-facing": StyledCheckboxLabelPatientFacing,
};

const CheckedIconMap = {
  default: CheckedIconDefault,
  "patient-facing": CheckedIconPatientFacing,
};

const UncheckedIconMap = {
  default: UncheckedIconDefault,
  "patient-facing": UncheckedIconPatientFacing,
};

const StyledContainer = styled.div`
  display: flex;
`;

export interface CheckboxType<T> extends FormFieldType<T> {
  theme: CheckboxThemeType["name"];
  label: string | JSX.Element;
  disabled?: boolean;
  readOnly?: boolean;
  hideDirty?: boolean;
  hideErrorText?: boolean;
  alignTop?: boolean;
  button?: boolean;
  size?: "small" | "medium";
  onChange?: () => void;
}

export function Checkbox<T>({
  theme,
  label,
  name,
  disabled,
  hideDirty,
  hideErrorText,
  alignTop,
  size = "medium",
  button,
  registerOptions = {},
  onChange,
  readOnly,
}: CheckboxType<T>) {
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const { formState, register } = useFormContext();

  const error = get(formState.errors, name);
  const hasError = Boolean(error);
  const isDirty = get(formState.dirtyFields, name);
  const isDisabled = disabled || formState.isSubmitting;
  const isReadOnly = readOnly || false;
  const isChecked = useWatch({ name }) === true;

  const StyledCheckboxLabel = styledCheckboxLabel[theme];
  const StyledCheckboxError = styledCheckboxError[theme];

  const CheckedIcon = CheckedIconMap[theme];
  const UncheckedIcon = UncheckedIconMap[theme];

  const Checkbox = isChecked ? CheckedIcon : UncheckedIcon;

  const handleFocus = useCallback(() => {
    setKeyboardFocus(true);
  }, []);

  const { onBlur, ...registerProps } = register(name, registerOptions);

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setKeyboardFocus(false);
      onBlur(event);
    },
    [onBlur]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      // Prevent clicks if readOnly
      if (isReadOnly) {
        event.preventDefault();
        return;
      }
      // Keyboard events will have [0,0], click events will not.
      setKeyboardFocus(event.screenX === 0 && event.screenY === 0);
    },
    [isReadOnly]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent changes if readOnly
      if (isReadOnly) {
        event.preventDefault();
        return;
      }
      registerProps.onChange(event);
      if (onChange) {
        onChange();
      }
    },
    [onChange, registerProps, isReadOnly]
  );

  const handleMouseMove = useCallback(() => {
    setKeyboardFocus(false);
  }, []);

  return (
    <>
      <StyledContainer data-testid="Checkbox">
        <StyledCheckboxLabel
          data-testid="Checkbox__Label"
          $hasError={hasError}
          $disabled={isDisabled}
          $readOnly={isReadOnly}
          $checked={isChecked}
          $keyboardFocus={keyboardFocus}
          $isDirty={hideDirty ? false : isDirty}
          $alignTop={alignTop}
          $size={size}
          $button={button}
        >
          <input
            {...registerProps}
            data-testid="Checkbox__Input"
            type="checkbox"
            checked={isChecked}
            disabled={isDisabled}
            aria-invalid={hasError}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={handleClick}
            onChange={handleChange}
            tabIndex={isReadOnly ? -1 : undefined}
          />
          {!button && (
            <Checkbox
              isDirty={isDirty}
              hideDirty={hideDirty}
              onMouseMove={handleMouseMove}
            />
          )}
          <div>{label}</div>
        </StyledCheckboxLabel>
      </StyledContainer>
      {!hideErrorText && error?.message && (
        <StyledCheckboxError
          data-testid="Checkbox__Error"
          $disabled={isDisabled}
        >
          {error.message}
        </StyledCheckboxError>
      )}
    </>
  );
}
